import axios from "axios"
import API from "@/utils/api"
import { buildPublicCustomerOrderDetailPath } from "@/services/public-storefront-paths"
import { mapOrderDetailsResponse, type OrderDetailsHttpEnvelope } from "@/domain/order/order-details.adapter"
import type { OrderDetailsUI } from "@/domain/order/order-details.model"

export type FetchOrderDetailsResult = "empty" | OrderDetailsUI

function mapOrderDetailsLoadError(error: unknown): Error {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    if (status === 401) {
      // Preserve axios error so the page can detect session expiry.
      return error
    }
    if (status === 404) {
      return new Error("Order not found.")
    }
    if (status === 403) {
      return new Error("You do not have access to this order.")
    }
    return new Error("Unable to load order details. Try again.")
  }
  if (error instanceof Error) return error
  return new Error("Unable to load order details. Try again.")
}

/**
 * Loads order details for My Orders → Order Details (customer-owned contract).
 * Uses the tenant-scoped GET /api/public/v1/storefront/orders/{orderId} — not the staff
 * GET /orders/v1/{orderId}, and not the legacy unscoped GET /orders/v1/customer/{orderId}.
 * Returns mapped {@link OrderDetailsUI} — never raw API payload.
 * On success: {@link OrderDetailsUI} or `"empty"`. On failure: throws (never returns undefined).
 * Callers own UX — interceptor toasts are suppressed.
 */
export async function fetchOrderDetailsUI(
  orderId: string,
  signal?: AbortSignal
): Promise<FetchOrderDetailsResult> {
  try {
    // Tenant-scoped storefront endpoint: an order belonging to another restaurant is 404 here, so
    // the boundary is enforced server-side rather than by anything this page does with the response.
    const res = await API.get<OrderDetailsHttpEnvelope>(
      `/${buildPublicCustomerOrderDetailPath(orderId)}`,
      { signal, skipErrorToast: true }
    )
    if (res == null || typeof res !== "object" || typeof (res as { status?: unknown }).status !== "number") {
      throw new Error("Invalid response from server")
    }
    if (res.status === 204) {
      return "empty"
    }
    const envelope = res.data
    if (envelope?.resultCode !== 200 || !envelope?.success) {
      throw new Error(typeof envelope?.message === "string" ? envelope.message : "Failed to load order")
    }
    return mapOrderDetailsResponse(envelope)
  } catch (error) {
    throw mapOrderDetailsLoadError(error)
  }
}
